const formula = (entry) => ({
  variables: {},
  units: "",
  notes: "",
  relatedCalculators: [],
  tags: [],
  source: "Industry standard formula",
  sourceEdition: "Not edition-specific",
  lastVerified: "2026-06-10",
  assumptions: [],
  ...entry,
});

export const FORMULA_REFERENCE_DATABASE = [
  formula({
    name: "Spindle Speed from Surface Speed",
    category: "Machining",
    formula: "RPM = (SFM x 12) / (pi x diameter) ~= (SFM x 3.82) / diameter",
    variables: { RPM: "spindle speed", SFM: "surface feet per minute", diameter: "tool diameter for mills/drills or stock diameter for lathes" },
    units: "SFM and inches",
    example: "300 SFM with a 0.500 in cutter: RPM = 300 x 3.82 / 0.5 = 2292 RPM",
    notes: "Use stock diameter for turning and tool diameter for rotating tools.",
    source: "Machinery's Handbook style standard shop math; also common tooling manufacturer formula",
    sourceEdition: "Formula is edition-stable; verify cutting-speed tables against latest available handbook/toolmaker data",
    assumptions: ["Diameter is in inches.", "Surface speed is in feet per minute."],
    relatedCalculators: ["feeds-speeds", "annular-cutter-speed"],
    tags: ["rpm", "sfm", "speed", "lathe", "mill", "drill"],
  }),
  formula({
    name: "Metric Spindle Speed",
    category: "Machining",
    formula: "RPM = (Vc x 1000) / (pi x diameter mm)",
    variables: { Vc: "cutting speed in m/min" },
    units: "m/min and millimeters",
    example: "120 m/min with 10 mm cutter: RPM = 120000 / 31.416 = 3820 RPM",
    notes: "Use manufacturer cutting speed by material, tool grade, coating, and coolant.",
    source: "Machinery's Handbook style standard shop math; ISO/metric tooling catalog convention",
    sourceEdition: "Formula is edition-stable; verify Vc tables against latest available handbook/toolmaker data",
    assumptions: ["Diameter is in millimeters.", "Cutting speed Vc is in meters per minute."],
    relatedCalculators: ["feeds-speeds"],
    tags: ["metric", "rpm", "m/min", "cutting speed"],
  }),
  formula({
    name: "Milling Feed Rate",
    category: "Machining",
    formula: "Feedrate = RPM x flutes x chip load",
    variables: { flutes: "number of cutting teeth", "chip load": "feed per tooth" },
    units: "IPM or mm/min",
    example: "3000 RPM x 4 flutes x 0.002 in/tooth = 24 IPM",
    notes: "For turning, feed is normally feed per revolution, not feed per minute.",
    source: "Machinery's Handbook style standard shop math; common milling practice",
    sourceEdition: "Formula is edition-stable",
    assumptions: ["Chip load is feed per tooth.", "Flute count is the number of effective cutting teeth."],
    relatedCalculators: ["feed-chipload", "feeds-speeds"],
    tags: ["feedrate", "chip load", "IPM", "mm/min"],
  }),
  formula({
    name: "Drilling Feed Rate",
    category: "Machining",
    formula: "Feedrate = RPM x feed per revolution",
    variables: { RPM: "spindle speed", "feed per revolution": "axial drill feed per spindle revolution" },
    units: "IPM or mm/min",
    example: "1200 RPM x 0.004 in/rev = 4.8 IPM",
    notes: "Drilling, reaming, tapping, and turning should not use milling chip-load-per-tooth logic unless the operation specifically uses multiple cutting teeth as chip load.",
    source: "Machinery's Handbook style standard shop math; common drill/tap/lathe feed convention",
    sourceEdition: "Formula is edition-stable; feed recommendations must be checked against current drill/tap/toolmaker data",
    assumptions: ["Feed per revolution is selected or calculated separately from material/tool data.", "Machine can feed at the calculated rate."],
    relatedCalculators: ["feeds-speeds", "tap-drill", "threading"],
    tags: ["drilling", "feed per revolution", "IPM", "mm/min"],
  }),
  formula({
    name: "Material Removal Rate",
    category: "Machining",
    formula: "MRR = width of cut x depth of cut x feedrate",
    variables: { MRR: "material removal rate" },
    units: "in^3/min or mm^3/min",
    example: "0.5 x 0.1 x 20 = 1.0 in^3/min",
    notes: "Rectangular milling estimate. Turning and drilling use different engagement geometry.",
    relatedCalculators: ["mrr", "horsepower"],
    tags: ["mrr", "roughing", "horsepower"],
  }),
  formula({
    name: "Cutting Horsepower",
    category: "Machining",
    formula: "Cutting HP = MRR x unit horsepower",
    variables: { "unit horsepower": "HP required per cubic inch per minute for material" },
    units: "HP",
    example: "1.0 in^3/min in mild steel at 1.0 unit HP = 1.0 cutting HP",
    notes: "Estimate only; actual spindle load depends on hardness, tool geometry, engagement, coating, coolant, and machine condition.",
    relatedCalculators: ["horsepower"],
    tags: ["horsepower", "mrr", "unit hp"],
  }),
  formula({
    name: "Tool Deflection Beam Estimate",
    category: "Machining",
    formula: "Deflection = F x L^3 / (3 x E x I); round tool I = pi x diameter^4 / 64",
    variables: { F: "cutting force", L: "stickout length", E: "modulus of elasticity", I: "area moment of inertia" },
    units: "consistent force and length units",
    example: "Doubling stickout increases deflection by 8x; doubling diameter increases stiffness by 16x.",
    notes: "Planning estimate only. Holder, collet, spindle, runout, and cutting-force model affect real deflection.",
    relatedCalculators: ["tool-deflection"],
    tags: ["tool deflection", "stickout", "stiffness", "chatter"],
  }),
  formula({
    name: "Drill Point Allowance",
    category: "Drilling & Holemaking",
    formula: "Allowance = (drill diameter / 2) / tan(point angle / 2)",
    variables: { "point angle": "included drill point angle" },
    units: "inch or mm",
    example: "0.25 drill, 118 deg point: allowance = 0.075 in",
    notes: "Estimates additional point depth for full diameter to reach depth.",
    relatedCalculators: ["drill-depth"],
    tags: ["drill depth", "118", "135", "point"],
  }),
  formula({
    name: "Countersink Depth",
    category: "Drilling & Holemaking",
    formula: "Depth = (top diameter - hole diameter) / (2 x tan(angle / 2))",
    variables: { angle: "included countersink angle" },
    units: "inch or mm",
    example: "(0.507 - 0.257) / (2 x tan 41 deg) = 0.144 in for 82 deg",
    notes: "Use measured screw head diameter for fit-sensitive work.",
    relatedCalculators: ["countersink"],
    tags: ["countersink", "flat head", "82", "90"],
  }),
  formula({
    name: "Tap Drill Approximation",
    category: "Threads & Tapping",
    formula: "Tap drill ~= major diameter - pitch",
    variables: { pitch: "1/TPI for inch or metric pitch" },
    units: "inch or mm",
    example: "1/4-20: 0.250 - 0.050 = 0.200 in, nearest common drill #7",
    notes: "Approximation for cutting taps. Use thread percent/class tables for final selection.",
    relatedCalculators: ["tap-drill", "thread-percentage"],
    tags: ["tap drill", "UNC", "UNF", "metric", "thread"],
  }),
  formula({
    name: "Unified Inch Thread Pitch",
    category: "Threads & Tapping",
    formula: "Pitch = 1 / threads per inch",
    variables: { TPI: "threads per inch" },
    units: "inch",
    example: "20 TPI pitch = 1 / 20 = .050 in",
    notes: "Unified thread acceptance depends on class, allowance, pitch diameter, and standard tables.",
    relatedCalculators: ["threading", "tap-drill"],
    tags: ["UNC", "UNF", "UN", "TPI", "pitch"],
  }),
  formula({
    name: "Metric Thread Pitch",
    category: "Threads & Tapping",
    formula: "Metric pitch is the axial distance from one thread crest to the next",
    variables: { pitch: "distance between thread crests in mm" },
    units: "mm",
    example: "M10 x 1.5 has 1.5 mm pitch",
    notes: "Metric thread class and tolerance must be checked from ISO thread tables for final acceptance.",
    relatedCalculators: ["threading", "tap-drill"],
    tags: ["metric thread", "ISO", "pitch", "M10"],
  }),
  formula({
    name: "Thread Percentage for 60 Degree Threads",
    category: "Threads & Tapping",
    formula: "% thread = ((major - tap drill) / 2) / (0.649519 x pitch)",
    variables: { pitch: "thread pitch" },
    units: "decimal percent",
    example: "1/4-20 with #7 drill: radial depth / theoretical depth ~= 75%",
    notes: "Simplified 60-degree thread math; class, plating, and forming taps need separate data.",
    relatedCalculators: ["thread-percentage", "tap-drill"],
    tags: ["percent thread", "tap", "60 degree"],
  }),
  formula({
    name: "True Position Diameter Error",
    category: "Metrology & Inspection",
    formula: "TP = 2 x sqrt((actual X - nominal X)^2 + (actual Y - nominal Y)^2)",
    variables: { TP: "diametrical true position error" },
    units: "inch or mm",
    example: "X error .002 and Y error -.002 gives TP = .0057",
    notes: "GD&T position is commonly a diametrical tolerance zone. Datum setup and modifiers still control acceptance.",
    relatedCalculators: ["true-position", "gdt"],
    tags: ["true position", "GD&T", "position", "inspection"],
  }),
  formula({
    name: "MMC Bonus Tolerance",
    category: "Metrology & Inspection",
    formula: "Hole bonus = actual size - MMC size; pin bonus = MMC size - actual size",
    units: "inch or mm",
    example: "0.503 hole with MMC 0.500 gives 0.003 bonus",
    notes: "Only applies when the feature control frame invokes MMC/LMC modifier.",
    relatedCalculators: ["mmc-bonus", "true-position"],
    tags: ["MMC", "bonus", "GD&T"],
  }),
  formula({
    name: "Worst-Case Tolerance Stack",
    category: "Metrology & Inspection",
    formula: "Worst-case band = sum of absolute contributing tolerances",
    units: "inch or mm",
    example: "+/-.005 plus +/-.003 creates +/-.008 worst-case range",
    notes: "Conservative method for guaranteed assembly limits.",
    relatedCalculators: ["tolerance-stack"],
    tags: ["tolerance stack", "worst case"],
  }),
  formula({
    name: "RSS Tolerance Stack",
    category: "Metrology & Inspection",
    formula: "RSS = sqrt(tol1^2 + tol2^2 + ...)",
    units: "inch or mm",
    example: "sqrt(.005^2 + .003^2) = .0058",
    notes: "Statistical estimate only when variation is independent and controlled.",
    relatedCalculators: ["tolerance-stack"],
    tags: ["RSS", "tolerance stack", "statistical"],
  }),
  formula({
    name: "Bend Allowance",
    category: "Sheet Metal & Fabrication",
    formula: "BA = angle radians x (inside radius + K-factor x thickness)",
    units: "inch or mm",
    example: "90 deg, R=.125, T=.125, K=.33 gives BA=.261 in",
    notes: "K-factor varies by material, tooling, bend method, and radius/thickness ratio.",
    relatedCalculators: ["bend-allowance"],
    tags: ["bend allowance", "K-factor", "sheet metal"],
  }),
  formula({
    name: "K-Factor",
    category: "Sheet Metal & Fabrication",
    formula: "K = ((bend allowance / angle radians) - inside radius) / thickness",
    variables: { K: "neutral axis location as a fraction of material thickness" },
    units: "unitless",
    example: "BA .2607, 90 deg, R .125, T .125 gives K about .33",
    notes: "Measured shop data is better than a generic default. Values outside 0 to 0.5 need review.",
    relatedCalculators: ["k-factor", "bend-allowance"],
    tags: ["K-factor", "neutral axis", "bend allowance"],
  }),
  formula({
    name: "Bend Deduction",
    category: "Sheet Metal & Fabrication",
    formula: "BD = 2 x OSSB - BA; OSSB = tan(angle / 2) x (inside radius + thickness)",
    units: "inch or mm",
    example: "Use with outside leg dimensions for a single bend flat pattern.",
    notes: "Flat pattern convention must match drawing dimensioning method.",
    relatedCalculators: ["bend-deduction", "flat-pattern"],
    tags: ["bend deduction", "flat pattern"],
  }),
  formula({
    name: "Press Brake Air-Bending Tonnage",
    category: "Sheet Metal & Fabrication",
    formula: "Tons/ft = (575 x tensile factor x thickness^2) / V-die opening",
    units: "US tons per foot",
    example: ".125 steel, V=1.0, tensile factor 1.0 gives 8.98 tons/ft",
    notes: "Verify with press brake/tooling manufacturer charts and material certs.",
    relatedCalculators: ["press-brake-tonnage"],
    tags: ["press brake", "tonnage", "air bend"],
  }),
  formula({
    name: "Kerf Offset",
    category: "CNC / G-Code",
    formula: "Offset = kerf width / 2",
    units: "inch or mm",
    example: ".060 kerf uses .030 path offset",
    notes: "Outside profiles offset outward; inside holes offset inward. CAM/controller compensation mode matters.",
    relatedCalculators: ["kerf-compensation"],
    tags: ["kerf", "plasma", "laser", "waterjet"],
  }),
  formula({
    name: "Arc Center I/J",
    category: "CNC / G-Code",
    formula: "I = center X - start X; J = center Y - start Y",
    units: "inch or mm",
    example: "Start X0 Y0, center X1 Y0 gives I1.0 J0.0",
    notes: "Most Fanuc/Haas controls use incremental I/J unless configured otherwise.",
    relatedCalculators: ["arc-center"],
    tags: ["IJK", "G02", "G03", "arc"],
  }),
  formula({
    name: "Bolt Circle Coordinates",
    category: "CNC / G-Code",
    formula: "X = centerX + radius x cos(angle); Y = centerY + radius x sin(angle)",
    variables: { angle: "start angle plus index spacing around 360 degrees" },
    units: "inch or mm",
    example: "4 holes on 4 in diameter at 0 deg start: first point X2.000 Y0.000",
    notes: "Coordinate sign and zero-angle convention must match the print and CNC setup.",
    relatedCalculators: ["bolt-pattern"],
    tags: ["bolt circle", "coordinates", "layout", "CNC"],
  }),
  formula({
    name: "Weld Heat Input",
    category: "Welding",
    formula: "Heat input = (volts x amps x 60 x efficiency) / (travel speed x 1000)",
    units: "kJ/in or kJ/mm",
    example: "24 V, 180 A, 8 in/min, 0.8 efficiency = 25.92 kJ/in",
    notes: "Code work must follow qualified WPS/PQR and actual procedure variables.",
    relatedCalculators: ["weld-heat-input"],
    tags: ["welding", "heat input", "GMAW", "SMAW", "GTAW"],
  }),
  formula({
    name: "Fillet Weld Volume",
    category: "Welding",
    formula: "Volume ~= 0.5 x leg size^2 x weld length x reinforcement factor",
    units: "in^3 or mm^3",
    example: "1/4 in fillet x 12 in x 1.1 = 0.4125 in^3",
    notes: "Actual volume depends on profile, passes, root opening, reinforcement, and fit-up.",
    relatedCalculators: ["weld-volume", "weld-filler-estimate"],
    tags: ["fillet", "weld volume", "filler"],
  }),
  formula({
    name: "Torque",
    category: "Mechanical Design",
    formula: "Torque = force x perpendicular distance",
    units: "lb-in, lb-ft, or N-m",
    example: "100 lbf at 12 in = 1200 lb-in = 100 lb-ft",
    notes: "Use perpendicular moment arm.",
    relatedCalculators: ["torque"],
    tags: ["torque", "moment", "force"],
  }),
  formula({
    name: "Bolt Torque Estimate",
    category: "Mechanical Design",
    formula: "T = K x D x F",
    units: "lb-in",
    example: "K=.2, D=.5, F=5000 gives 500 lb-in",
    notes: "Nut factor varies strongly with lubrication, plating, washer surface, and thread condition.",
    relatedCalculators: ["bolt-torque"],
    tags: ["bolt torque", "clamp load", "fastener"],
  }),
  formula({
    name: "Bearing L10 Life",
    category: "Mechanical Design",
    formula: "L10 = (C/P)^p million revolutions",
    units: "million revolutions or hours",
    example: "C/P=5, ball bearing p=3 gives 125 million rev",
    notes: "Basic rating only; lubrication, contamination, shock, temperature, and reliability factors matter.",
    relatedCalculators: ["bearing-life"],
    tags: ["bearing", "L10", "life"],
  }),
  formula({
    name: "Gear Pitch Diameter",
    category: "Gears / Pulleys / Power Transmission",
    formula: "Pitch diameter = number of teeth / diametral pitch",
    units: "inch",
    example: "40 teeth / 10 DP = 4.000 in PD",
    notes: "Verify pressure angle, profile shift, and gear system.",
    relatedCalculators: ["gear-pitch-diameter", "gear-ratio"],
    tags: ["gear", "DP", "pitch diameter"],
  }),
  formula({
    name: "Gear Ratio",
    category: "Gears / Pulleys / Power Transmission",
    formula: "Ratio = driven teeth / driver teeth",
    units: "unitless",
    example: "60 driven teeth / 20 driver teeth = 3:1 reduction",
    notes: "Idler gears change direction but not the final ratio in a simple train.",
    relatedCalculators: ["gear-ratio", "chain-sprocket-ratio"],
    tags: ["gear ratio", "speed ratio", "teeth"],
  }),
  formula({
    name: "Metric Module Gear",
    category: "Gears / Pulleys / Power Transmission",
    formula: "Pitch diameter mm = teeth x module",
    units: "mm",
    example: "40 teeth x module 2 = 80 mm PD",
    notes: "Standard spur gear approximation.",
    relatedCalculators: ["module-gear"],
    tags: ["module", "metric gear"],
  }),
  formula({
    name: "Pulley RPM",
    category: "Gears / Pulleys / Power Transmission",
    formula: "Driven RPM = driver RPM x driver diameter / driven diameter",
    units: "RPM",
    example: "1750 RPM x 3 / 6 = 875 RPM",
    notes: "Uses pitch diameter approximation and ignores slip.",
    relatedCalculators: ["pulley-rpm"],
    tags: ["pulley", "belt", "rpm"],
  }),
  formula({
    name: "Circle Area",
    category: "Shop Math / Trig / Geometry",
    formula: "Area = pi x diameter^2 / 4",
    units: "square units",
    example: "1.000 in diameter area = 0.7854 in^2",
    relatedCalculators: ["circle-calculator"],
    tags: ["circle", "area", "diameter"],
  }),
  formula({
    name: "Chord Height",
    category: "Shop Math / Trig / Geometry",
    formula: "Sagitta = R - sqrt(R^2 - (chord / 2)^2)",
    units: "inch or mm",
    example: "R2, chord2 gives sagitta .268",
    relatedCalculators: ["chord-height"],
    tags: ["chord", "sagitta", "radius"],
  }),
  formula({
    name: "Material Weight",
    category: "Materials",
    formula: "Weight = volume x density",
    units: "lb or kg",
    example: "12 x 6 x .25 steel plate = 18 in^3 x .283 = 5.094 lb",
    notes: "Use actual material cert/supplier data for critical quotes.",
    relatedCalculators: ["material-weight", "plate-weight", "bar-stock-weight"],
    tags: ["weight", "density", "material"],
  }),
  formula({
    name: "Linear Thermal Expansion",
    category: "Materials",
    formula: "Change in length = coefficient x original length x temperature change",
    units: "inch or mm",
    example: "12 in steel, 52 F rise, alpha about 6.7e-6/F gives .0042 in",
    notes: "Coefficient changes with alloy and temperature range.",
    relatedCalculators: ["thermal-expansion"],
    tags: ["thermal expansion", "temperature", "inspection"],
  }),
  formula({
    name: "Machine Time",
    category: "Costing / Estimating",
    formula: "Total hours = setup hours + (cycle minutes x quantity / 60)",
    units: "hours",
    example: "1 setup hr + 6 min x 25 / 60 = 3.5 hr",
    relatedCalculators: ["machine-time", "job-quote"],
    tags: ["machine time", "cycle", "quote"],
  }),
  formula({
    name: "Margin Price",
    category: "Costing / Estimating",
    formula: "Price = cost / (1 - margin)",
    units: "currency",
    example: "$100 cost at 30% margin = $142.86 price",
    notes: "Margin and markup are not the same.",
    relatedCalculators: ["markup-margin", "part-cost-estimate"],
    tags: ["margin", "markup", "quote"],
  }),
  formula({
    name: "OEE",
    category: "Costing / Estimating",
    formula: "OEE = availability x performance x quality",
    units: "percent",
    example: "90% x 85% x 98% = 74.97%",
    notes: "Use consistent definitions by work center.",
    relatedCalculators: ["oee"],
    tags: ["OEE", "production", "capacity"],
  }),
];

export const FORMULA_CATEGORIES = Array.from(
  new Set(FORMULA_REFERENCE_DATABASE.map((item) => item.category))
);
